import {useEffect, useRef, useState} from 'react';
import {getPageConfig} from '../../config/pageConfigs';
import {getOrderedPersonas, PERSONAS} from '../../config/personas';
import {ACCOUNT_ID} from '../../api/account';
import {createVideoGeneration, generateText as apiGenerateText, getGenerationStatus} from '../../api/video';
import {trackEvent} from '../../utils/analytics';
import {getYandexClientId} from '../../utils/telegram';
import {showError} from '../../utils/notify';

// Star Card Component
function StarCard({star, isSelected, onSelect, isMuted, onToggleMute}) {
    const videoRef = useRef(null);
    const [isVideoLoaded, setIsVideoLoaded] = useState(false);

    // Play/pause video based on selection
    useEffect(() => {
        if (videoRef.current) {
            if (isSelected) {
                videoRef.current.play().catch(() => {
                });
            } else {
                videoRef.current.pause();
                videoRef.current.currentTime = 0;
                setIsVideoLoaded(false);
            }
        }
    }, [isSelected]);

    // Update muted state
    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.muted = isMuted;
        }
    }, [isMuted]);

    return (
        <div
            onClick={() => onSelect(star)}
            className="flex-shrink-0 cursor-pointer w-[140px]"
        >
            <div
                className={`rounded-[20px] bg-white w-full relative transition-all p-[10px] ${isSelected ? 'ring-2 ring-brand-red ring-offset-0' : ''}`}>
                <div className="h-[180px] rounded-[10px] overflow-hidden relative">
                    {/* Always show image as background */}
                    <img
                        src={star.image}
                        alt={star.name}
                        className={`w-full h-full object-cover object-top absolute inset-0 transition-opacity duration-300 ${isSelected && isVideoLoaded ? 'opacity-0' : 'opacity-100'}`}
                    />
                    {/* Video overlay when selected */}
                    {isSelected && star.video && (
                        <>
                            <video
                                ref={videoRef}
                                src={star.video}
                                className={`w-full h-full object-cover object-top absolute inset-0 transition-opacity duration-300 ${isVideoLoaded ? 'opacity-100' : 'opacity-0'}`}
                                loop
                                muted={isMuted}
                                playsInline
                                onCanPlay={() => setIsVideoLoaded(true)}
                            />
                            {/* Loading spinner while video loads */}
                            {!isVideoLoaded && (
                                <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                                    <div
                                        className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                </div>
                            )}
                            {/* Mute/Unmute button */}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onToggleMute?.();
                                }}
                                className="absolute bottom-2 right-2 w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center hover:bg-black/70 transition z-10"
                            >
                                {isMuted ? (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white"
                                         strokeWidth="2">
                                        <path d="M11 5L6 9H2v6h4l5 4V5z"/>
                                        <line x1="23" y1="9" x2="17" y2="15"/>
                                        <line x1="17" y1="9" x2="23" y2="15"/>
                                    </svg>
                                ) : (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white"
                                         strokeWidth="2">
                                        <path d="M11 5L6 9H2v6h4l5 4V5z"/>
                                        <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/>
                                    </svg>
                                )}
                            </button>
                        </>
                    )}
                </div>
                <div className="pt-[10px] flex items-end justify-between">
                    <p className="text-sm font-medium text-brand-dark leading-[1.3] tracking-[-0.14px] w-[85px]">
                        {star.name}
                    </p>
                    {isSelected && (
                        <div
                            className="w-6 h-6 rounded-full bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange flex items-center justify-center">
                            <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                                <path d="M1 5L4.5 8.5L11 1" stroke="white" strokeWidth="2" strokeLinecap="round"
                                      strokeLinejoin="round"/>
                            </svg>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}


// Occasion Card Component
function OccasionCard({occasion, isSelected, onSelect}) {
    return (
        <button
            type="button"
            onClick={() => onSelect(occasion)}
            className={`flex-shrink-0 w-[150px] h-[80px] rounded-[15px] p-[10px] flex flex-col justify-between transition-all bg-white cursor-pointer ${isSelected ? 'ring-2 ring-brand-red' : 'hover:ring-1 hover:ring-brand-red/30'}`}
        >
            <div className="flex justify-between items-start">
                <span className="text-xl">{occasion.icon}</span>
                {isSelected && (
                    <div
                        className="w-5 h-5 rounded-full bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange flex items-center justify-center">
                        <svg width="10" height="8" viewBox="0 0 12 10" fill="none">
                            <path d="M1 5L4.5 8.5L11 1" stroke="white" strokeWidth="2" strokeLinecap="round"
                                  strokeLinejoin="round"/>
                        </svg>
                    </div>
                )}
            </div>
            <p className="text-sm font-medium text-brand-dark leading-[1.2] tracking-[-0.12px] text-left">
                {occasion.name}
            </p>
        </button>
    );
}

// Scroll Arrows
function ScrollArrows({onPrev, onNext}) {
    return (
        <div className="flex gap-[5px]">
            <button
                onClick={onPrev}
                className="w-[40px] h-[28px] rounded-lg bg-white flex items-center justify-center hover:bg-gray-50 transition"
            >
                <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
                    <path d="M7 1L1 7L7 13" stroke="#221d2d" strokeWidth="2" strokeLinecap="round"
                          strokeLinejoin="round"/>
                </svg>
            </button>
            <button
                onClick={onNext}
                className="w-[40px] h-[28px] rounded-lg bg-white flex items-center justify-center hover:bg-gray-50 transition"
            >
                <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
                    <path d="M1 1L7 7L1 13" stroke="#221d2d" strokeWidth="2" strokeLinecap="round"
                          strokeLinejoin="round"/>
                </svg>
            </button>
        </div>
    );
}


// LocalStorage key for saving generated text
const GENERATED_TEXT_KEY = 'celebs_generated_text';

// Main Interface Card
export default function MainCard({
                                     credits,
                                     selectedPersonaId,
                                     onPersonaChange,
                                     onGenerationStart,
                                     onGenerationComplete,
                                     onAccountStatusUpdate,
                                     onNoCredits,
                                     hideBack = false,
                                     isExampleVideoPlaying = false,
                                     onStopExampleVideo
                                 }) {
    const pageConfig = getPageConfig();

    // Get ordered personas (preselected first)
    const orderedPersonas = getOrderedPersonas(pageConfig.preSelectedPersonaId);

    // Form state - derive selectedPersona from controlled prop
    const selectedPersona = PERSONAS.find(p => p.id === selectedPersonaId) || orderedPersonas[0];

    const handlePersonaSelect = (persona) => {
        onPersonaChange?.(persona.id);
    };
    const [generatedText, setGeneratedText] = useState(() => {
        return localStorage.getItem(GENERATED_TEXT_KEY) || '';
    });

    // AI generation state - occasions already come reordered from pageConfig
    const [selectedOccasion, setSelectedOccasion] = useState(pageConfig.occasions[0]);
    const [occasionText, setOccasionText] = useState(pageConfig.occasions[0].name);
    const [isOccasionDropdownOpen, setIsOccasionDropdownOpen] = useState(false);
    // Pre-fill aiDetails with recipient if present
    const [aiDetails, setAiDetails] = useState(
        pageConfig.preSelectedRecipient ? `Поздравление ${pageConfig.preSelectedRecipient}` : ''
    );
    const [isGeneratingAi, setIsGeneratingAi] = useState(false);

    // Video mute state for selected persona
    const [isVideoMuted, setIsVideoMuted] = useState(true);

    // Mute main video when example video starts playing
    useEffect(() => {
        if (isExampleVideoPlaying) {
            setIsVideoMuted(true);
        }
    }, [isExampleVideoPlaying]);

    // Yandex Metrica client ID for Telegram deeplink
    const [yandexClientId, setYandexClientId] = useState(null);

    const starsRef = useRef(null);
    const occasionsRef = useRef(null);
    const occasionDropdownRef = useRef(null);
    const occasionInputRef = useRef(null);
    const detailsInputRef = useRef(null);

    // Fetch Yandex client ID on mount
    useEffect(() => {
        getYandexClientId().then(setYandexClientId);
    }, []);

    // Handle occasion card selection
    const handleOccasionSelect = (occasion) => {
        setSelectedOccasion(occasion);
        setIsOccasionDropdownOpen(false);
        if (occasion.id === 'custom') {
            setOccasionText('');
            setTimeout(() => occasionInputRef.current?.focus(), 100);
        } else {
            setOccasionText(occasion.name);
            setTimeout(() => detailsInputRef.current?.focus(), 100);
        }
    };

    // Handle custom text input - deselect card if text differs
    const handleOccasionTextChange = (e) => {
        const newText = e.target.value;
        setOccasionText(newText);
        setIsOccasionDropdownOpen(false);
        // Deselect card if text doesn't match any option
        const matchingOption = pageConfig.occasions.find(o => o.name === newText);
        setSelectedOccasion(matchingOption || null);
    };

    // Close occasions dropdown on outside click
    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (occasionDropdownRef.current && !occasionDropdownRef.current.contains(event.target)) {
                setIsOccasionDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, []);

    // Save generated text to localStorage when it changes
    useEffect(() => {
        localStorage.setItem(GENERATED_TEXT_KEY, generatedText);
    }, [generatedText]);

    // Handle text changes
    const handleTextChange = (e) => {
        setGeneratedText(e.target.value);
    };

    // Get the concept for AI generation
    const getAiConcept = () => {
        return occasionText.trim();
    };

    // Generate text with AI (with 3 retries)
    const handleGenerateWithAi = async () => {
        const concept = getAiConcept();
        if (!concept.trim()) {
            showError('Пожалуйста, укажите тему поздравления');
            return;
        }

        trackEvent('generate_text');
        setIsGeneratingAi(true);

        try {
            const data = await apiGenerateText({
                celebId: selectedPersona.id,
                concept: concept,
                details: aiDetails,
            });
            setGeneratedText(data.text);
        } catch (error) {
            console.error('AI generation error:', error);
            showError('Ошибка генерации текста. Попробуйте ещё раз.');
        } finally {
            setIsGeneratingAi(false);
        }
    };

    // Poll job status
    const pollJobStatus = async (jobId) => {
        while (true) {
            try {
                const data = await getGenerationStatus(jobId);
                if (data.status === 'completed') {
                    onGenerationComplete?.(data.video_url);
                    onAccountStatusUpdate?.();
                    return;
                } else if (data.status === 'failed') {
                    showError(`Ошибка: ${data.error || 'Не удалось сгенерить видео'}`);
                    onGenerationComplete?.(null);
                    onAccountStatusUpdate?.();
                    return;
                }
                await new Promise(resolve => setTimeout(resolve, 5000));
            } catch (error) {
                console.error('Polling error:', error);
                showError('Ошибка проверки статуса. Попробуйте ещё раз.');
                onGenerationComplete?.(null);
                return;
            }
        }
    };

    // Handle form submission
    const handleSubmit = async () => {
        if (!selectedPersona) {
            showError('Пожалуйста, выберите звезду');
            return;
        }

        if (!generatedText.trim()) {
            showError('Пожалуйста, сгенерируйте текст поздравления');
            return;
        }

        // Check if user has credits
        if (credits <= 0) {
            onNoCredits?.(selectedPersona, generatedText);
            return;
        }

        try {
            const jobData = await createVideoGeneration({
                celebId: selectedPersona.id,
                wishText: generatedText,
                accountId: ACCOUNT_ID,
            });
            const jobId = jobData.id;

            // Update account status (credits and history)
            onAccountStatusUpdate?.();

            // Start generation animation and poll for status
            onGenerationStart?.(selectedPersona);
            pollJobStatus(jobId);
        } catch (error) {
            if (error.data?.error === 'insufficient_credits') {
                onNoCredits?.(selectedPersona, generatedText);
                return;
            }
            showError('Ошибка создания заказа. Попробуйте ещё раз.');
            console.error(error);
        }
    };

    const scrollStars = (direction) => {
        if (starsRef.current) {
            const scrollAmount = direction === 'next' ? 300 : -300;
            starsRef.current.scrollBy({left: scrollAmount, behavior: 'smooth'});
        }
    };

    const scrollOccasions = (direction) => {
        if (occasionsRef.current) {
            const scrollAmount = direction === 'next' ? 200 : -200;
            occasionsRef.current.scrollBy({left: scrollAmount, behavior: 'smooth'});
        }
    };

    return (
        <div className="max-w-[1110px] mx-auto px-4">
            <div className="bg-brand-gray rounded-[20px] md:rounded-[30px] p-0 relative">
                <div className="flex flex-col lg:flex-row">
                    {/* Left side - Selectors and inputs */}
                    <div className="flex-1 min-w-0 p-4 pb-4 lg:p-[30px]">
                        {/* Star Selector */}
                        <div className="mb-6">
                            <div className="flex items-center justify-between mb-4 pr-4 lg:pr-0">
                                <h3 className="text-xl md:text-2xl font-bold text-brand-dark leading-[1.1] tracking-[-0.24px]">
                                    Выберите звезду
                                </h3>
                                <ScrollArrows
                                    onPrev={() => scrollStars('prev')}
                                    onNext={() => scrollStars('next')}
                                />
                            </div>
                            <div className="relative overflow-hidden">
                                <div
                                    ref={starsRef}
                                    className="flex gap-[5px] overflow-x-scroll no-scrollbar py-1 pl-1"
                                    style={{scrollBehavior: 'smooth'}}
                                >
                                    {orderedPersonas.map(star => (
                                        <StarCard
                                            key={star.id}
                                            star={star}
                                            isSelected={selectedPersona?.id === star.id}
                                            onSelect={handlePersonaSelect}
                                            isMuted={isVideoMuted}
                                            onToggleMute={() => {
                                                if (isVideoMuted && isExampleVideoPlaying) {
                                                    // User is unmuting while example is playing - stop example
                                                    onStopExampleVideo?.();
                                                }
                                                setIsVideoMuted(!isVideoMuted);
                                            }}
                                        />
                                    ))}
                                    {/* Spacer for fade effect */}
                                    <div className="flex-shrink-0 w-[100px]"/>
                                </div>
                                <div
                                    className="absolute right-0 top-0 bottom-0 w-[120px] gradient-fade-right pointer-events-none"/>
                            </div>
                        </div>

                        {/* Occasion Selector */}
                        <div className="mb-4">
                            <div className="flex items-center justify-between mb-4 pr-4 lg:pr-0">
                                <h3 className="text-xl md:text-2xl font-bold text-brand-dark leading-[1.1] tracking-[-0.24px]">
                                    Выберите повод
                                </h3>
                                <ScrollArrows
                                    onPrev={() => scrollOccasions('prev')}
                                    onNext={() => scrollOccasions('next')}
                                />
                            </div>
                            <div className="relative overflow-hidden">
                                <div
                                    ref={occasionsRef}
                                    className="flex gap-[5px] overflow-x-scroll no-scrollbar py-1 pl-1"
                                    style={{scrollBehavior: 'smooth'}}
                                >
                                    {pageConfig.occasions.map(occasion => (
                                        <OccasionCard
                                            key={occasion.id}
                                            occasion={occasion}
                                            isSelected={selectedOccasion?.id === occasion.id}
                                            onSelect={handleOccasionSelect}
                                        />
                                    ))}
                                    <div className="flex-shrink-0 w-[80px]"/>
                                </div>
                                <div
                                    className="absolute right-0 top-0 bottom-0 w-[100px] gradient-fade-right pointer-events-none"/>
                            </div>
                        </div>
                    </div>

                    {/* Right side - inputs, text and submit */}
                    <div className="w-full lg:w-[420px] lg:flex-shrink-0">
                        <div
                            className="bg-white rounded-[20px] m-2 p-4 lg:m-[10px] lg:p-[20px] h-auto lg:h-[calc(100%-20px)] flex flex-col">

                            {/* Theme and Details inputs - vertical */}
                            <div className="flex flex-col gap-3 mb-4">
                                <div className="relative" ref={occasionDropdownRef}>
                                    <input
                                        ref={occasionInputRef}
                                        type="text"
                                        value={occasionText}
                                        onChange={handleOccasionTextChange}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Escape') {
                                                setIsOccasionDropdownOpen(false);
                                            }
                                        }}
                                        placeholder="Тема поздравления..."
                                        className="w-full pl-4 pr-12 py-3 rounded-[10px] border border-[#d3d2d5] text-base font-medium placeholder:text-brand-dark/50 focus:outline-none focus:border-brand-red transition bg-brand-gray"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setIsOccasionDropdownOpen(prev => !prev)}
                                        aria-label="Выбрать повод"
                                        className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 w-8 rounded-md text-brand-dark/70 hover:bg-black/5 transition flex items-center justify-center"
                                    >
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            className={`transition-transform ${isOccasionDropdownOpen ? 'rotate-180' : ''}`}
                                        >
                                            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2"
                                                  strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </button>

                                    {isOccasionDropdownOpen && (
                                        <div
                                            className="absolute z-30 top-full mt-1 w-full rounded-[10px] border border-[#d3d2d5] bg-white shadow-lg overflow-hidden">
                                            <div className="max-h-56 overflow-y-auto py-1">
                                                {pageConfig.occasions.map(occasion => (
                                                    <button
                                                        key={occasion.id}
                                                        type="button"
                                                        onClick={() => handleOccasionSelect(occasion)}
                                                        className={`w-full px-4 py-2.5 text-left text-sm font-medium transition flex items-center gap-2 ${
                                                            occasion.id === 'custom'
                                                                ? `border-t border-[#ecebf0] ${
                                                                    selectedOccasion?.id === occasion.id
                                                                        ? 'bg-[#f1f1f3] text-[#5b5961]'
                                                                        : 'bg-[#f7f7f8] text-[#7c7a84] hover:bg-[#f1f1f3]'
                                                                }`
                                                                : selectedOccasion?.id === occasion.id
                                                                    ? 'bg-brand-red/10 text-brand-dark'
                                                                    : 'text-brand-dark hover:bg-black/5'
                                                        }`}
                                                    >
                                                        <span>{occasion.icon}</span>
                                                        <span>
                                                            {occasion.id === 'custom' ? 'Свой текст' : occasion.name}
                                                            {occasion.id === 'custom' && (
                                                                <span
                                                                    className="block text-xs font-normal text-[#8f8d97]">
                                                                    Ввести вручную
                                                                </span>
                                                            )}
                                                        </span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <input
                                    ref={detailsInputRef}
                                    type="text"
                                    value={aiDetails}
                                    onChange={(e) => setAiDetails(e.target.value)}
                                    placeholder="Имя, возраст, детали..."
                                    className="w-full px-4 py-3 rounded-[10px] border border-[#d3d2d5] text-base font-medium placeholder:text-brand-dark/50 focus:outline-none focus:border-brand-red transition bg-brand-gray"
                                />
                                <button
                                    onClick={handleGenerateWithAi}
                                    disabled={!occasionText.trim() || isGeneratingAi}
                                    className="w-full py-3 rounded-xl bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white text-sm font-medium tracking-[-0.14px] hover:opacity-95 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {isGeneratingAi ? (
                                        <>
                                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10"
                                                        stroke="currentColor" strokeWidth="4" fill="none"/>
                                                <path className="opacity-75" fill="currentColor"
                                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                                            </svg>
                                            Генерируем...
                                        </>
                                    ) : (
                                        <>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                 stroke="currentColor" strokeWidth="2">
                                                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                                            </svg>
                                            Сгенерить текст
                                        </>
                                    )}
                                </button>
                            </div>

                            {/* Generated text area */}
                            <div className="mb-4 flex-1">
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-sm font-medium text-brand-dark">Текст</span>
                                    <span
                                        className="text-sm text-brand-dark/50 tracking-[-0.14px]">{generatedText.length}/250</span>
                                </div>
                                <textarea
                                    value={generatedText}
                                    onChange={handleTextChange}
                                    placeholder="Сгенерируйте текст или напишите свой..."
                                    maxLength={250}
                                    className="w-full h-[190px] px-4 py-3 rounded-[10px] border border-[#d3d2d5] text-base font-medium placeholder:text-brand-dark/50 focus:outline-none focus:border-brand-red transition resize-none"
                                />
                            </div>

                            <button
                                onClick={handleSubmit}
                                disabled={!selectedPersona || !generatedText.trim()}
                                className="w-full py-4 rounded-2xl bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white text-sm font-medium tracking-[-0.14px] hover:opacity-95 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {credits > 0
                                    ? `Сгенерить видео (${credits} осталось)`
                                    : 'Сгенерить видео'
                                }
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
